import React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { useRouter } from 'hooks/useRouter'
import { getAccessToken } from 'lib/auth'
// constants
import { POWERBI_EXPORT_STATUSES } from 'constants/powerBI'
// graphql
import { POWER_BI_CHECK_EXPORT_QUERY, POWER_BI_REPORTS_QUERY } from 'graphql/powerBI/queries'
import { POWER_BI_START_EXPORT_MUTATION } from 'graphql/powerBI/mutations'
// hooks
import { useApolloClient } from 'hooks/useApolloClient'
import { IPowerBiReport } from 'types/graphql'
// types
import { Report as IReport } from 'report'
// utils
import filter from 'lodash/fp/filter'
import get from 'lodash/get'
import map from 'lodash/fp/map'
import pipe from 'lodash/fp/pipe'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type Props = React.PropsWithChildren<unknown>

interface ReportPage {
  pageName: string
  bookmark: { state: string }
}

interface Context {
  enterFullscreen: () => void
  exportToFile: (format: string) => void
  isExporting: boolean
  report: IPowerBiReport
  reportRef: React.MutableRefObject<IReport>
  setPages: (pageName: string) => void
}

export const ReportAPIContext = React.createContext<Context>({} as any)

const ReportAPI: React.FC<Props> = ({ children }) => {
  const [exportId, setExportId] = React.useState<string>('')
  const [exportStatus, setExportStatus] = React.useState<string>(POWERBI_EXPORT_STATUSES.NOT_STARTED)
  const [fileFormat, setFileFormat] = React.useState<string>('')
  const [isExporting, setIsExporting] = React.useState<boolean>(false)
  const [timerId, setTimerId] = React.useState<number>(null)
  const [currentPages, setCurrentPages] = React.useState<ReportPage[]>([])

  const reportRef = React.useRef<IReport>()

  const { client } = useApolloClient()

  const router = useRouter()

  const accessToken = getAccessToken()

  const { data: reportData } = useQuery(POWER_BI_REPORTS_QUERY, {
    fetchPolicy: 'network-only',
    skip: !router.query.id,
    variables: { filter: { ids: router.query.id } }
  })

  const report: IPowerBiReport = get(reportData, 'powerBIReports.data[0]', {})

  // methods
  const enterFullscreen = () => reportRef.current.fullscreen()

  const exportToFile = async (format) => {
    try {
      setIsExporting(true)

      const capturedBookmark = await reportRef.current.bookmarksManager.capture({
        personalizeVisuals: true
      })

      const reportPages = await reportRef.current.getPages()

      const pages = pipe(
        map(({ name }) => ({ pageName: name, bookmark: { state: capturedBookmark.state } })),
        filter(({ pageName }) => currentPages.every((page) => pageName !== page.pageName)),
        (result) => [...result, ...currentPages],
        (result) => {
          const sortingOrder = reportPages.map(({ name }) => name)

          return result.sort((a, b) => sortingOrder.indexOf(a.pageName) - sortingOrder.indexOf(b.pageName))
        }
      )(reportPages)

      const exportId = get(
        await client.mutate({
          mutation: POWER_BI_START_EXPORT_MUTATION,
          variables: {
            input: {
              reportId: report.id,
              pages,
              format
            }
          }
        }),
        'data.powerBIStartExportToFile.data.exportId'
      )

      setFileFormat(format)
      setExportId(exportId)
    } catch (e) {
      setIsExporting(false)
      console.log('[ReportAPI.exportToFile]', parseGraphQLErrorMessages(e))
    }
  }

  const checkExportStatus = async () => {
    try {
      const status = get(
        await client.query({
          fetchPolicy: 'network-only',
          query: POWER_BI_CHECK_EXPORT_QUERY,
          variables: {
            input: {
              exportId,
              reportId: report.id
            }
          }
        }),
        'data.powerBICheckExportToFileStatus.data.status'
      )

      console.log('Status: ', status)

      setExportStatus(status)
    } catch (e) {
      setIsExporting(false)
      console.log('[ReportAPI.checkExportStatus]', parseGraphQLErrorMessages(e))
    }
  }

  const downloadFile = async () => {
    try {
      const url = `${import.meta.env.VITE_GQL_API_URL}/api/exportReport/${report.id}/${exportId}`

      const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })

      setIsExporting(false)
      setExportStatus(POWERBI_EXPORT_STATUSES.NOT_STARTED)

      const blob = await res.blob()
      const newBlob = new Blob([blob])
      const blobUrl = window.URL.createObjectURL(newBlob)
      const link = document.createElement('a')

      link.href = blobUrl
      link.setAttribute('download', `${report.name}.${fileFormat.toLowerCase()}`)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)

      // @ts-ignore
      window.URL.revokeObjectURL(blob)
    } catch (e) {
      console.log('ReportAPI.downloadFile', e)
    }
  }

  const setPages = async (pageName) => {
    const capturedBookmark = await reportRef.current.bookmarksManager.capture({
      personalizeVisuals: true
    })

    const currentPage = { pageName, bookmark: { state: capturedBookmark.state } }

    setCurrentPages((state) => {
      if (state.some(({ pageName }) => pageName === currentPage.pageName)) {
        const updatedPages = state.map((page) => {
          if (page.pageName === currentPage.pageName) {
            return currentPage
          }
          return page
        })

        return updatedPages
      }
      return [...state, currentPage]
    })
  }

  React.useEffect(() => {
    if (exportId) {
      setTimerId(setInterval(() => checkExportStatus(), 10000) as any)

      return () => clearTimeout(timerId)
    }
  }, [exportId])

  React.useEffect(() => {
    if (exportStatus === POWERBI_EXPORT_STATUSES.SUCCEEDED) {
      clearTimeout(timerId)
      downloadFile()
    }
  }, [exportStatus, fileFormat, timerId])

  const value = { enterFullscreen, exportToFile, isExporting, report, reportRef, setPages }

  return <ReportAPIContext.Provider value={value}>{children}</ReportAPIContext.Provider>
}

export default ReportAPI
